import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { AtSign, Loader2, MessageCircle, X, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { CodeNameTag } from '../components/CodeNameTag';

const fmtTime = (iso) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function MentionsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const markedRef = useRef(false);

  const load = () => {
    api.getMentionsInbox().then(r => {
      setData(r.data);
      // Viewing the inbox clears the badge — the items stay until dismissed
      if (!markedRef.current && (r.data.unread_total || 0) > 0) {
        markedRef.current = true;
        api.markAllMentionsRead().catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const dismissAll = async () => {
    setBusy(true);
    try {
      await api.dismissAllMentions();
      toast.success('All mentions dismissed');
      load();
    } catch { toast.error('Failed'); }
    setBusy(false);
  };

  const dismissGroup = async (projectId) => {
    try {
      await api.dismissDealMentions(projectId);
      setData(prev => {
        const groups = (prev?.groups || []).filter(g => g.project_id !== projectId);
        return { ...prev, groups, total: groups.reduce((a, g) => a + g.items.length, 0) };
      });
    } catch { toast.error('Failed to dismiss'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;
  }

  const groups = data?.groups || [];
  const newCount = data?.unread_total || 0;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="mentions-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
            <AtSign className="w-7 h-7 text-blue-400" /> Mentions
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {data?.total
              ? `${data.total} mention${data.total !== 1 ? 's' : ''} across ${groups.length} deal${groups.length !== 1 ? 's' : ''}${newCount > 0 ? ` — ${newCount} new` : ''} · stays here until you dismiss it`
              : 'Comments where teammates @-mentioned you'}
          </p>
        </div>
        {groups.length > 0 && (
          <Button onClick={dismissAll} disabled={busy} variant="outline" data-testid="mentions-dismiss-all-btn"
            className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8 px-3">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
            Dismiss all
          </Button>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="border border-border rounded-sm bg-card p-12 text-center" data-testid="mentions-empty">
          <AtSign className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No mentions — you're all caught up.</p>
          <p className="text-xs text-muted-foreground/60 mt-1">When a teammate mentions you with @ in a deal discussion, it shows up here until you dismiss it.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(g => (
            <div key={g.project_id} className="bg-card border border-border rounded-sm overflow-hidden" data-testid={`mentions-group-${g.project_id}`}>
              <div className="flex items-center bg-secondary/40">
                <button onClick={() => navigate(`/projects/${g.project_id}#section-discussion`)}
                  data-testid={`mentions-group-link-${g.project_id}`}
                  className="flex-1 min-w-0 flex items-center gap-2 px-4 py-2.5 hover:bg-secondary/70 transition-colors text-left flex-wrap">
                  <span className="text-sm font-medium text-foreground">{g.code_name ? <CodeNameTag name={g.code_name} /> : g.name}</span>
                  {g.company_name && <span className="text-[11px] text-muted-foreground truncate max-w-[240px]">· {g.company_name}</span>}
                  <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] text-blue-400 font-mono">
                    <MessageCircle className="w-3 h-3" /> {g.items.length} · open discussion →
                  </span>
                </button>
                <button onClick={() => dismissGroup(g.project_id)} title="Dismiss these mentions"
                  data-testid={`mentions-dismiss-${g.project_id}`}
                  className="shrink-0 px-3 py-2.5 text-muted-foreground/60 hover:text-foreground transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="divide-y divide-border/60">
                {g.items.map(c => (
                  <button key={c.id} onClick={() => navigate(`/projects/${g.project_id}#section-discussion`)}
                    className="w-full flex gap-2.5 px-4 py-2.5 hover:bg-secondary/30 transition-colors text-left">
                    <div className="w-6 h-6 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-[9px] font-bold font-mono shrink-0 mt-0.5">
                      {c.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">{c.user_name}</span>
                        <span className="text-[9px] text-muted-foreground/50 font-mono">{fmtTime(c.created_at)}</span>
                        {!c.seen && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-wider text-blue-400"
                            data-testid={`mention-new-${c.id}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" /> new
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-foreground/80 mt-0.5 line-clamp-2">{c.text}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
